/**
 * LivingStructureBackground - World Class Minimal Edition
 * 
 * Ultra-subtle, refined background
 * - Barely visible grid that adds depth
 * - Soft, monochromatic glow
 * - No distractions
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

interface LivingStructureBackgroundProps {
  className?: string;
}

// Minimal Perspective Grid
const MinimalGrid: React.FC = () => {
  const gridRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const animationRef = useRef<number | null>(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = gridRef.current;
    if (!canvas || !dimensions.width || !dimensions.height) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const animate = () => {
      timeRef.current += 0.001;
      const breathe = Math.sin(timeRef.current) * 0.1 + 0.9;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const horizonY = canvas.height * 0.12;
      const bottomY = canvas.height * 0.85;
      
      // Ultra subtle color with hint of violet
      const gridColor = `rgba(180, 160, 220, ${0.035 * breathe})`;
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 0.5;

      // Horizontal lines - minimal
      const horizontalLines = 12;
      for (let i = 0; i <= horizontalLines; i++) {
        const progress = i / horizontalLines;
        const y = horizonY + (bottomY - horizonY) * Math.pow(progress, 2);
        
        const perspectiveScale = 0.02 + progress * 0.98;
        const lineWidth = canvas.width * 1.5 * perspectiveScale;
        const startX = centerX - lineWidth / 2;
        const endX = centerX + lineWidth / 2;

        const opacity = Math.pow(progress, 0.5) * (1 - progress * 0.3);
        
        if (opacity > 0.05) {
          ctx.globalAlpha = opacity * 0.7 * breathe;
          ctx.beginPath();
          ctx.moveTo(startX, y);
          ctx.lineTo(endX, y);
          ctx.stroke();
        }
      }

      // Vertical lines - minimal
      const verticalLines = 20;
      const gridSpacing = 120;
      for (let i = -verticalLines / 2; i <= verticalLines / 2; i++) {
        const bottomX = centerX + i * gridSpacing;
        const topX = centerX + (i * gridSpacing * 0.02);
        
        const distFromCenter = Math.abs(i) / (verticalLines / 2);
        const opacity = Math.max(0, 1 - distFromCenter * 0.8);

        if (opacity > 0.05) {
          ctx.globalAlpha = opacity * 0.6 * breathe;
          ctx.beginPath();
          ctx.moveTo(bottomX, bottomY);
          ctx.lineTo(topX, horizonY);
          ctx.stroke();
        }
      }

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions]);

  return (
    <canvas
      ref={gridRef}
      className="absolute inset-0 w-full h-full"
    />
  );
};

// Abstract Architectural Elements - World-Class Minimalist
const LegalSkyline: React.FC = () => {
  return (
    <div className="absolute bottom-0 left-0 right-0 h-[400px] pointer-events-none overflow-hidden">
      {/* Abstract vertical lines suggesting columns/pillars */}
      <div className="absolute bottom-0 left-0 right-0 h-full flex items-end justify-between px-[5%]">
        {/* Left cluster */}
        <div className="flex items-end gap-[2px]">
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.08] to-transparent"
            style={{ height: '120px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.12] to-transparent"
            style={{ height: '180px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.06] to-transparent"
            style={{ height: '140px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        {/* Left-center cluster */}
        <div className="flex items-end gap-[3px]">
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.1] to-transparent"
            style={{ height: '200px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.14] to-transparent"
            style={{ height: '260px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.1] to-transparent"
            style={{ height: '220px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.08] to-transparent"
            style={{ height: '180px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        {/* Center - empty space for content */}
        <div className="w-[40%]" />

        {/* Right-center cluster */}
        <div className="flex items-end gap-[3px]">
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.08] to-transparent"
            style={{ height: '190px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.12] to-transparent"
            style={{ height: '240px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.14] to-transparent"
            style={{ height: '280px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.1] to-transparent"
            style={{ height: '210px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.26, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        {/* Right cluster */}
        <div className="flex items-end gap-[2px]">
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.06] to-transparent"
            style={{ height: '150px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.1] to-transparent"
            style={{ height: '170px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.div 
            className="w-[1px] bg-gradient-to-t from-white/[0.08] to-transparent"
            style={{ height: '130px' }}
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ opacity: 1, scaleY: 1 }}
            transition={{ duration: 1.2, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
      </div>
    </div>
  );
};

const LivingStructureBackground: React.FC<LivingStructureBackgroundProps> = ({ className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 overflow-hidden pointer-events-none ${className}`}
      style={{ zIndex: 0 }}
    >
      {/* Grid */}
      <motion.div className="absolute inset-0" style={{ opacity }}>
        <MinimalGrid />
      </motion.div>

      {/* Purple mesh gradient orbs - matching login page */}
      {/* Secondary orb - bottom left */}
      <motion.div 
        className="absolute -bottom-[30%] -left-[15%] w-[900px] h-[900px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.12) 0%, rgba(99, 102, 241, 0.04) 40%, transparent 70%)',
        }}
        animate={{
          x: [0, -20, 0],
          y: [0, -30, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      {/* Accent orb - center */}
      <motion.div 
        className="absolute top-[30%] left-[40%] w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.08) 0%, transparent 60%)',
        }}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Subtle bottom vignette */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to top, rgba(8, 8, 10, 0.5) 0%, transparent 40%)'
        }}
      />
    </div>
  );
};

export default LivingStructureBackground;
